import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronUp, DollarSign, Download, Route, TrendingDown, Truck } from 'lucide-react';
import api from '../../lib/api';
import { formatDateOnly, ordenarVisitasRota } from '../../lib/utils';
import DespesasMotoristaPlaceholder from './DespesasMotoristaPlaceholder';
import {
  buildResumoPorCliente,
  buildSimulacaoConsolidadoExemplo,
  buildSimulacaoRota7Clientes,
  buildManutencaoIndex,
  getEconomiaRota,
  getMetodo2Value,
  getStandAloneTotal,
  rotaStatusColor,
  rotaStatusLabel,
  safeNumber,
  toCurrency,
} from '../../lib/relatorioCusto';

const RelatorioCustoDeslocamento = () => {
  const [loading, setLoading] = useState(true);
  const [rotas, setRotas] = useState([]);
  const [manutencoes, setManutencoes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [carros, setCarros] = useState([]);
  const [expandedRotaIds, setExpandedRotaIds] = useState(new Set());
  const [periodo, setPeriodo] = useState({
    data_inicio: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    data_fim: new Date().toISOString().split('T')[0],
  });
  const [tecnicoId, setTecnicoId] = useState('');
  const [carroId, setCarroId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [statusRota, setStatusRota] = useState('');
  const [simulacaoAtiva, setSimulacaoAtiva] = useState(false);
  const [simulacaoConsolidadoAtiva, setSimulacaoConsolidadoAtiva] = useState(false);
  const [modoVisao, setModoVisao] = useState('cliente');

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [usersRes, carrosRes] = await Promise.all([
          api.get('/users', { params: { funcao: 'tecnico' } }),
          api.get('/carros', { params: { limit: 500 } }),
        ]);
        setTecnicos(usersRes.data?.users || []);
        setCarros(carrosRes.data?.carros || []);
      } catch {
        // opções de filtro falham em silêncio
      }
    };
    loadOptions();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const rotasParams = {
          data_inicio: periodo.data_inicio,
          data_fim: periodo.data_fim,
          tecnico_id: tecnicoId || undefined,
          carro_id: carroId || undefined,
        };

        const [rotasRes, manutencoesRes] = await Promise.all([
          api.get('/rotas', { params: rotasParams }),
          api.get('/manutencoes', {
            params: {
              data_inicio: periodo.data_inicio,
              data_fim: periodo.data_fim,
              limit: 1000,
            },
          }),
        ]);

        setRotas(Array.isArray(rotasRes.data) ? rotasRes.data : []);
        setManutencoes(manutencoesRes.data?.manutencoes || []);
      } catch (error) {
        toast.error('Erro ao carregar relatório de custo de deslocamento');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [periodo, tecnicoId, carroId]);

  const rotasBaseComSimulacao = useMemo(() => {
    let base = [...rotas];
    if (simulacaoConsolidadoAtiva) {
      const { rotas: rotasCons } = buildSimulacaoConsolidadoExemplo();
      base = [...rotasCons, ...base];
    }
    if (simulacaoAtiva) base = [buildSimulacaoRota7Clientes(), ...base];
    return base;
  }, [rotas, simulacaoAtiva, simulacaoConsolidadoAtiva]);

  const clientesDisponiveis = useMemo(() => {
    const map = new Map();
    rotasBaseComSimulacao.forEach((rota) => {
      (rota.visitas || []).forEach((visita) => {
        if (!visita.cliente_id) return;
        if (!map.has(visita.cliente_id)) {
          map.set(visita.cliente_id, {
            id: visita.cliente_id,
            nome: visita.cliente_nome || visita.cliente_codigo || visita.cliente_id,
          });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [rotasBaseComSimulacao]);

  const mesmoClienteId = (a, b) => String(a) === String(b);

  const rotasFiltradas = useMemo(() => {
    return rotasBaseComSimulacao.filter((rota) => {
      if (statusRota && rota.status !== statusRota) return false;
      if (clienteId && !(rota.visitas || []).some((v) => mesmoClienteId(v.cliente_id, clienteId))) return false;
      return true;
    });
  }, [rotasBaseComSimulacao, statusRota, clienteId]);

  const manutencoesEfetivas = useMemo(() => {
    if (!simulacaoConsolidadoAtiva) return manutencoes;
    const { manutencoes: simMan } = buildSimulacaoConsolidadoExemplo();
    return [...simMan, ...manutencoes];
  }, [manutencoes, simulacaoConsolidadoAtiva]);

  const manutencaoIndex = useMemo(() => {
    const rotaIds = rotasFiltradas.map((r) => r.id);
    return buildManutencaoIndex(manutencoesEfetivas, rotaIds);
  }, [manutencoesEfetivas, rotasFiltradas]);

  const resumo = useMemo(() => {
    return rotasFiltradas.reduce(
      (acc, rota) => {
        const standAlone = getStandAloneTotal(rota);
        const custoRota = safeNumber(rota.custo_total);
        const economia = getEconomiaRota(rota);

        acc.totalRotas += 1;
        acc.totalKm += safeNumber(rota.km_total);
        acc.totalCusto += custoRota;
        acc.totalStandAlone += standAlone;
        acc.totalEconomia += economia;
        return acc;
      },
      { totalRotas: 0, totalKm: 0, totalCusto: 0, totalStandAlone: 0, totalEconomia: 0 }
    );
  }, [rotasFiltradas]);

  const resumoClientes = useMemo(
    () => buildResumoPorCliente(rotasFiltradas, manutencaoIndex),
    [rotasFiltradas, manutencaoIndex]
  );

  /** Com filtro de cliente: mostra só a linha desse cliente (antes parecia que “nada mudava”). */
  const resumoClientesParaExibicao = useMemo(() => {
    if (!clienteId) return resumoClientes;
    return resumoClientes.filter((row) => mesmoClienteId(row.cliente_id, clienteId));
  }, [resumoClientes, clienteId]);

  const resumoClientesCards = useMemo(() => {
    return resumoClientesParaExibicao.reduce(
      (acc, row) => {
        acc.totalClientes += 1;
        acc.totalVisitas += row.visitas;
        acc.totalMetodo2 += row.metodo2_total;
        acc.totalStandAlone += row.standalone_total;
        acc.totalEconomia += row.economia_total;
        acc.totalMateriais += row.materiais_total;
        acc.totalEstimado += row.metodo2_estimado_count;
        return acc;
      },
      {
        totalClientes: 0,
        totalVisitas: 0,
        totalMetodo2: 0,
        totalStandAlone: 0,
        totalEconomia: 0,
        totalMateriais: 0,
        totalEstimado: 0,
      }
    );
  }, [resumoClientesParaExibicao]);

  const buildExportRows = () => {
    return resumoClientesParaExibicao.map((row) => ({
      cliente: `${row.cliente_codigo} - ${row.cliente_nome}`,
      visitas: row.visitas,
      metodo2_oficial: row.metodo2_total.toFixed(2),
      standalone: row.standalone_total.toFixed(2),
      economia: row.economia_total.toFixed(2),
      media_metodo2: row.metodo2_medio.toFixed(2),
      media_standalone: row.standalone_medio.toFixed(2),
      materiais: row.materiais_total.toFixed(2),
      metodo2_fonte: row.metodo2_100_estimado ? 'Estimado (proporcional)' : 'Trecho/mesclado',
    }));
  };

  const exportarCsvClientes = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.error('Sem dados para exportar');
      return;
    }
    const headers = Object.keys(rows[0]);
    const content = [
      headers.join(';'),
      ...rows.map((r) => headers.map((h) => `${r[h]}`.replace('.', ',')).join(';')),
    ].join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_clientes_${periodo.data_inicio}_${periodo.data_fim}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exportação CSV iniciada');
  };

  const exportarXlsxClientes = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.error('Sem dados para exportar');
      return;
    }
    const headers = Object.keys(rows[0]);
    const html =
      `<table><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr>` +
      rows
        .map((r) => `<tr>${headers.map((h) => `<td>${r[h]}</td>`).join('')}</tr>`)
        .join('') +
      '</table>';
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_clientes_${periodo.data_inicio}_${periodo.data_fim}.xls`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exportação Excel iniciada');
  };

  const toggleExpand = (rotaId) => {
    setExpandedRotaIds((prev) => {
      const next = new Set(prev);
      if (next.has(rotaId)) next.delete(rotaId);
      else next.add(rotaId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="relatorio-custo-deslocamento">
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Filtros profissionais</h2>
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setSimulacaoAtiva((prev) => !prev)}
            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
              simulacaoAtiva
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {simulacaoAtiva ? 'Remover simulação (9 clientes)' : 'Carregar exemplo (9 clientes)'}
          </button>
          <button
            type="button"
            onClick={() => setSimulacaoConsolidadoAtiva((prev) => !prev)}
            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
              simulacaoConsolidadoAtiva
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {simulacaoConsolidadoAtiva
              ? 'Remover exemplo consolidado (3 rotas)'
              : 'Carregar exemplo consolidado (3 rotas)'}
          </button>
          {simulacaoAtiva && (
            <span className="text-xs text-slate-500">
              Exemplo: Base -> Ribeirao Preto -> 9 clientes (inclui Paulinia e Campinas 2km base) -> Base
            </span>
          )}
          {simulacaoConsolidadoAtiva && (
            <span className="text-xs text-slate-500">
              3 rotas fictícias: Campinas aparece em todas (3 visitas); materiais por rota+cliente para testar o consolidado.
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Data início</label>
            <input
              type="date"
              value={periodo.data_inicio}
              onChange={(e) => setPeriodo({ ...periodo, data_inicio: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Data fim</label>
            <input
              type="date"
              value={periodo.data_fim}
              onChange={(e) => setPeriodo({ ...periodo, data_fim: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Técnico</label>
            <select
              value={tecnicoId}
              onChange={(e) => setTecnicoId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white"
            >
              <option value="">Todos</option>
              {tecnicos.map((tecnico) => (
                <option key={tecnico.id} value={tecnico.id}>
                  {tecnico.name || tecnico.email || tecnico.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Carro</label>
            <select
              value={carroId}
              onChange={(e) => setCarroId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white"
            >
              <option value="">Todos</option>
              {carros.map((carro) => (
                <option key={carro.id} value={carro.id}>
                  {carro.placa || carro.modelo || carro.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Cliente</label>
            <select
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white"
              data-testid="custo-desloc-cliente-filter"
            >
              <option value="">Todos</option>
              {clientesDisponiveis.map((cliente) => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nome}
                </option>
              ))}
            </select>
            {clienteId ? (
              <p className="text-xs text-slate-500 mt-2">
                Mostrando apenas este cliente no consolidado e, em &quot;Por rota&quot;, só as visitas dele na expansão.
              </p>
            ) : null}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Status da rota</label>
            <select
              value={statusRota}
              onChange={(e) => setStatusRota(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white"
            >
              <option value="">Todos</option>
              <option value="rascunho">Rascunho</option>
              <option value="em_andamento">Em andamento</option>
              <option value="finalizada">Finalizada</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-3">
            <Route className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm font-medium text-slate-600">Rotas</p>
          <p className="text-2xl font-bold text-slate-900">{resumo.totalRotas}</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-2xl p-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mb-3">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm font-medium text-slate-600">KM total</p>
          <p className="text-2xl font-bold text-slate-900">{resumo.totalKm.toFixed(0)} km</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-3">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm font-medium text-slate-600">Custo total da rota</p>
          <p className="text-2xl font-bold text-slate-900">{toCurrency(resumo.totalCusto)}</p>
        </div>
        <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center mb-3">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm font-medium text-slate-600">Soma stand-alone</p>
          <p className="text-2xl font-bold text-slate-900">{toCurrency(resumo.totalStandAlone)}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-3">
            <TrendingDown className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm font-medium text-slate-600">Economia da roteirização</p>
          <p className="text-2xl font-bold text-slate-900">{toCurrency(resumo.totalEconomia)}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex bg-slate-100 rounded-xl p-1">
          <button
            type="button"
            onClick={() => setModoVisao('cliente')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              modoVisao === 'cliente' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Por cliente (consolidado)
          </button>
          <button
            type="button"
            onClick={() => setModoVisao('rota')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              modoVisao === 'rota' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Por rota
          </button>
        </div>
        {modoVisao === 'cliente' && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportarXlsxClientes}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700"
            >
              <Download className="w-4 h-4" /> Exportar Excel
            </button>
            <button
              type="button"
              onClick={exportarCsvClientes}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50"
            >
              <Download className="w-4 h-4" /> Exportar CSV
            </button>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
        Método 2 é o custo oficial por cliente no relatório. Quando `custo_trecho` não existir, o sistema usa `custo_proporcional` como fallback estimado.
      </div>

      {modoVisao === 'cliente' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-5">
              <p className="text-sm font-medium text-slate-600">Clientes</p>
              <p className="text-2xl font-bold text-slate-900">{resumoClientesCards.totalClientes}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5">
              <p className="text-sm font-medium text-slate-600">Método 2 (oficial)</p>
              <p className="text-2xl font-bold text-slate-900">{toCurrency(resumoClientesCards.totalMetodo2)}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-5">
              <p className="text-sm font-medium text-slate-600">Stand-alone (comparativo)</p>
              <p className="text-2xl font-bold text-slate-900">{toCurrency(resumoClientesCards.totalStandAlone)}</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5">
              <p className="text-sm font-medium text-slate-600">Economia total</p>
              <p className="text-2xl font-bold text-slate-900">{toCurrency(resumoClientesCards.totalEconomia)}</p>
            </div>
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-5">
              <p className="text-sm font-medium text-slate-600">Materiais (R$)</p>
              <p className="text-2xl font-bold text-slate-900">{toCurrency(resumoClientesCards.totalMateriais)}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Consolidado por cliente</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Cliente</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Visitas</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">
                      Método 2 <span className="text-xs text-emerald-700">(Oficial)</span>
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">
                      Stand-alone <span className="text-xs text-slate-500">(Comparativo)</span>
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Economia</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Média M2/visita</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Média SA/visita</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Materiais</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Fonte M2</th>
                  </tr>
                </thead>
                <tbody>
                  {resumoClientesParaExibicao.map((row) => (
                    <tr key={row.cliente_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-slate-900">{row.cliente_nome}</p>
                        <p className="text-xs text-slate-500">{row.cliente_codigo || row.cliente_id}</p>
                      </td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900">{row.visitas}</td>
                      <td className="py-3 px-4 text-sm text-right font-semibold text-emerald-700">{toCurrency(row.metodo2_total)}</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900">{toCurrency(row.standalone_total)}</td>
                      <td className="py-3 px-4 text-sm text-right font-medium text-emerald-700">{toCurrency(row.economia_total)}</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900">{toCurrency(row.metodo2_medio)}</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900">{toCurrency(row.standalone_medio)}</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900">{toCurrency(row.materiais_total)}</td>
                      <td className="py-3 px-4 text-sm text-right">
                        {row.metodo2_100_estimado ? (
                          <span className="inline-flex px-2 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-800">
                            Estimado
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 rounded-lg text-xs font-medium bg-emerald-100 text-emerald-800">
                            Trecho
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {resumoClientesParaExibicao.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-8 px-4 text-center text-slate-500">
                        Nenhum cliente encontrado para os filtros selecionados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Relatório profissional de custo por rota e cliente</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Rota</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">KM</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Custo rota</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Stand-alone</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Economia</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Por trecho</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {rotasFiltradas.map((rota) => {
                const expanded = expandedRotaIds.has(rota.id);
                const visitasOrdenadas = ordenarVisitasRota(rota.visitas);
                const visitas = clienteId
                  ? visitasOrdenadas.filter((v) => mesmoClienteId(v.cliente_id, clienteId))
                  : visitasOrdenadas;
                const standAlone = clienteId
                  ? visitas.reduce((acc, v) => acc + safeNumber(v.custo_real), 0)
                  : getStandAloneTotal(rota);
                const totalMetodo2 = visitas.reduce((acc, v) => acc + getMetodo2Value(v).valor, 0);
                const economia = clienteId
                  ? Math.max(0, standAlone - totalMetodo2)
                  : getEconomiaRota(rota);
                return (
                  <React.Fragment key={rota.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-slate-900">{formatDateOnly(rota.data)}</p>
                        <p className="text-xs text-slate-500">{rota.tecnico_nome} · {rota.carro_placa}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${rotaStatusColor[rota.status] || rotaStatusColor.rascunho}`}>
                          {rotaStatusLabel[rota.status] || rota.status || '—'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900">{safeNumber(rota.km_total).toFixed(0)} km</td>
                      <td className="py-3 px-4 text-sm text-right font-medium text-emerald-700">{toCurrency(rota.custo_total)}</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900">{toCurrency(standAlone)}</td>
                      <td className="py-3 px-4 text-sm text-right text-amber-700">{toCurrency(economia)}</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-700">{toCurrency(totalMetodo2)}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          type="button"
                          onClick={() => toggleExpand(rota.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                        >
                          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          {expanded ? 'Ocultar' : 'Abrir'}
                        </button>
                      </td>
                    </tr>

                    {expanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={8} className="p-0">
                          <div className="bg-slate-50 px-4 py-4">
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b border-slate-200">
                                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Ordem</th>
                                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Cliente</th>
                                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Stand-alone</th>
                                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Proporcional</th>
                                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Por trecho</th>
                                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Materiais usados</th>
                                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Custo materiais</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visitas.map((visita) => {
                                    const key = `${rota.id}::${visita.cliente_id}`;
                                    const material = manutencaoIndex.get(key);
                                    return (
                                      <tr key={visita.id} className="border-b border-slate-100">
                                        <td className="py-2 px-3 text-sm text-slate-700">{visita.ordem || '—'}</td>
                                        <td className="py-2 px-3">
                                          <p className="text-sm font-medium text-slate-900">{visita.cliente_nome}</p>
                                          <p className="text-xs text-slate-500">{visita.cliente_codigo}</p>
                                        </td>
                                        <td className="py-2 px-3 text-sm text-right text-slate-900">{toCurrency(visita.custo_real)}</td>
                                        <td className="py-2 px-3 text-sm text-right text-slate-900">{toCurrency(visita.custo_proporcional)}</td>
                                        <td className="py-2 px-3 text-sm text-right text-slate-700">{toCurrency(getMetodo2Value(visita).valor)}</td>
                                        <td className="py-2 px-3 text-xs text-slate-700">
                                          {material?.pecas?.length ? (
                                            <div className="space-y-1">
                                              {material.pecas.slice(0, 3).map((peca, idx) => (
                                                <p key={`${visita.id}-${idx}`}>
                                                  {peca.nome} ({peca.quantidade}) - {toCurrency(peca.valor_total)}
                                                </p>
                                              ))}
                                              {material.pecas.length > 3 && (
                                                <p className="text-slate-500">+{material.pecas.length - 3} item(ns)</p>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-slate-400">—</span>
                                          )}
                                        </td>
                                        <td className="py-2 px-3 text-sm text-right text-slate-900">
                                          {material ? toCurrency(material.custo_pecas_total) : '—'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {visitas.length === 0 && (
                                    <tr>
                                      <td colSpan={7} className="py-4 px-3 text-center text-sm text-slate-500">
                                        Nenhuma visita nesta rota.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {rotasFiltradas.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 px-4 text-center text-slate-500">
                    Nenhuma rota encontrada para os filtros selecionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </div>
      )}

      <DespesasMotoristaPlaceholder />
    </div>
  );
};

export default RelatorioCustoDeslocamento;
